import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { createEmptyCommunityInvite } from '../../../utils/formatUtils';
import {
  CommunityInvite,
  CustomFieldType,
  CustomMultipleChoiceField,
} from '../../../models/inviteService.model';
import InviteForm from '../../components/forms/InviteForm';
import inviteService from '../../../services/community/inviteService';
import dashboardService from '../../../services/community/communityService';
import { filterCustomLabelFields } from '../../../utils/filterUtils';
import connectionService from '../../../services/community/connectionService';
import Button from '../../components/buttons/Button';
import useCommunityEffect from '../../../hooks/effects/useCommunityEffect';
import WarningModal from '../../components/modals/WarningModal';
import DisableMemberAdministrationModal from '../../components/modals/DisableMemberAdministrationModal';

export default function RequestedDataPage(): JSX.Element {
  const [invite, setInvite] = useState<CommunityInvite>(createEmptyCommunityInvite());
  const [action, setAction] = useState<'CREATE' | 'UPDATE'>('CREATE');
  const [showNoDataModal, setShowNoDataModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const { customFields } = invite;

  const community = useSelectedCommunity();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useCommunityEffect(() => {
    const fetchInvite = async () => {
      const fields = await inviteService.fetchInvite();
      if (!fields) return;
      setInvite(fields);
      setAction('UPDATE');
    };
    fetchInvite();
    connectionService.getConnections();
  }, []);

  const handleCancel = async (): Promise<void> => {
    setInvite(community?.inviteFields || createEmptyCommunityInvite());
    navigate('/contacts', { state: { tab: 'Info' } });
  };

  const handleSubmit = async (): Promise<void> => {
    const hasSelectedFields = 
      invite.mandatoryFields.length > 0 || 
      invite.optionalFields.length > 0 || 
      invite.customFields.length > 0;

    if (!hasSelectedFields) {
      setShowNoDataModal(true);
      return;
    }

    const labelFields = filterCustomLabelFields(customFields);
    if (
      labelFields.some((field): boolean => !field.label) ||
      customFields
        .filter((x) => x.type === CustomFieldType.MULTIPLE_CHOICE)
        .map((x) => (x as CustomMultipleChoiceField).options)
        .flat()
        .some((option): boolean => !option)
    ) {
      toast.error(t('toast.error.allFields'));
      return;
    }
    if (
      Array.from(new Set(labelFields.map((field) => field.label))).length !== labelFields.length
    ) {
      toast.error(t('toast.error.noTwoSame'));
      return;
    }

    if (action === 'CREATE') await inviteService.createInvite(invite);
    if (action === 'UPDATE')
      await inviteService.updateInvite(invite).then(() => connectionService.getConnections(true));

    if (invite.nonRequestedFields.length !== 10) {
      //set the member adminstration feature to true if it is not already
      const features = { ...(community?.features || {}) };
      if (!features.MEMBER_ADMINISTRATION) {
        features.MEMBER_ADMINISTRATION = true;
        await dashboardService.updateCommunity(
          community?.name || '',
          community?.description || '',
          community?.memberPermissions || {},
          features,
        );
      }
    }

    await dashboardService.fetchCommunity();

    toast.success(
      action === 'UPDATE' ? t('toast.success.inviteUpdated') : t('toast.success.inviteCreated'),
    );
    if (action === 'CREATE') navigate('/invite-link', { state: { view: 'SETUP' } });
    else navigate('/contacts', { state: { tab: 'Info' } });
  };

  const handleDisableMemberAdministration = async (): Promise<void> => {
    try {
      // Clear all invite data
      const clearedInvite = {
        ...invite,
        mandatoryFields: [],
        optionalFields: [],
        nonRequestedFields: [...invite.nonRequestedFields, ...invite.mandatoryFields, ...invite.optionalFields],
        customFields: [],
      };
      
      await inviteService.updateInvite(clearedInvite);
      
      // Disable the member administration feature
      const features = { ...(community?.features || {}) };
      features.MEMBER_ADMINISTRATION = false;

      await dashboardService.updateCommunity(
        community?.name || '',
        community?.description || '',
        (community?.memberPermissions || {}) as import('../../../types/community').CommunityMemberPermissions,
        features,
      );
      
      await dashboardService.fetchCommunity();
      
      toast.success(t('toast.success.communitySaved'));
      navigate('/contacts');
    } catch (error) {
      console.error('Failed to disable member administration:', error);
      toast.error(t('toast.error.unexpected'));
    }
  };

  return (
    <>
      {/* Header */}
      <div className="mb-6 flex flex-col border-b border-primary-300 pb-6">
        <h1 className="mb-2 text-3xl font-semibold">
          {t('page.requestedData.title')}
        </h1>
        <p className="lg:w-1/2">
          {community?.inviteId
            ? t('page.requestedData.subtitle')
            : t('page.settings.subtitle', { type: 'community' })}
        </p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="w-full pb-4">
          <InviteForm invite={invite} setInvite={setInvite} />
        </div>
        <div className="mt-auto flex w-full flex-col justify-between">
          <div className="mb-4 flex w-full border-b border-secondary-200 pb-4 lg:mb-0 lg:mt-0 lg:justify-end lg:border-b-0 lg:pb-0">
            <Button
              onClick={handleCancel}
              className="px-0 font-medium underline disabled:opacity-20">
              {t('general.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSubmit} className="ml-6 disabled:opacity-20">
              {t('general.save')}
            </Button>
          </div>
        </div>
      </div>
      
      <WarningModal
        open={showNoDataModal}
        setOpen={setShowNoDataModal}
        title={t('toast.error.noDataSelected')}
        description={t('page.requestedData.noDataModal.description')}
        button={t('page.requestedData.noDataModal.selectData')}
        cancel={t('page.requestedData.noDataModal.disableFeature')}
        onCancel={() => {
          setShowNoDataModal(false);
          setShowDisableModal(true);
        }}
      />
      
      <DisableMemberAdministrationModal
        open={showDisableModal}
        setOpen={setShowDisableModal}
        currentInvite={invite}
        onConfirmDisable={handleDisableMemberAdministration}
      />
    </>
  );
}
